import { Controller } from "@hotwired/stimulus"
import { Calendar } from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"

// A visual calendar layered on top of the plain "Upcoming rotation" table and override form
// (organizations/on_call_schedules/show.html.erb), not a replacement for either: both stay fully
// keyboard/screen-reader usable, which a drag-and-drop calendar genuinely isn't without a lot
// more work than this pass is trying to do. Two kinds of blocks, distinguished by color and by
// FullCalendar's own per-event editable flag: computed rotation periods (dark gray, read-only,
// the scheduling algorithm's own output, not something to drag) and persisted overrides (blue,
// draggable/resizable for an admin, matching the same OnCallOverridePolicy#update? check
// eventsValue was already built from server-side).
//
// Every event's start/end arrives as a "floating" wall-clock string with no UTC offset, already
// converted server-side to the viewer's own chosen time zone (see OnCallSchedulesController#
// floating_local_string); this controller never converts between real-world zones itself, only
// ever reads/writes the same floating wall-clock numbers FullCalendar's own Date objects already
// carry (getFullYear/getHours/etc, never toISOString or a UTC getter, which would silently
// reinterpret them through the browser's own OS zone instead).
export default class extends Controller {
  static values = { events: Array, editable: Boolean, updateUrl: String }

  connect() {
    this.calendar = new Calendar(this.element, {
      plugins: [ dayGridPlugin, interactionPlugin ],
      initialView: "dayGridMonth",
      headerToolbar: { left: "prev,next today", center: "title", right: "" },
      height: "auto",
      events: this.eventsValue,
      editable: this.editableValue,
      selectable: this.editableValue,
      select: (info) => this.fillOverrideForm(info),
      eventDrop: (info) => this.persistDrag(info),
      eventResize: (info) => this.persistDrag(info)
    })
    this.calendar.render()
  }

  disconnect() {
    this.calendar?.destroy()
  }

  // Dragging across empty calendar days doesn't create anything itself; it populates the
  // existing, fully accessible override form below with the picked range and focuses the member
  // select, so confirming it is still a real, visible form submission an admin reviews before it
  // takes effect, not a silent AJAX create.
  fillOverrideForm(info) {
    const startsAtField = document.getElementById("on_call_override_starts_at")
    const endsAtField = document.getElementById("on_call_override_ends_at")
    if (startsAtField) startsAtField.value = this.toDatetimeLocalValue(info.start)
    if (endsAtField) endsAtField.value = this.toDatetimeLocalValue(info.end)
    document.getElementById("on_call_override_user_id")?.focus()
    this.calendar.unselect()
  }

  async persistDrag(info) {
    const id = info.event.id.replace("override-", "")
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(`${this.updateUrlValue}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({
          starts_at: this.toFloatingString(info.event.start),
          ends_at: this.toFloatingString(info.event.end)
        })
      })
      const result = await response.json()
      if (!result.ok) {
        alert(result.error || "Could not update this override.")
        info.revert()
      }
    } catch {
      // A dropped save reverts the block back to where it was; the next drag (or a plain reload)
      // tries again, the same "don't leave the UI showing something the server never actually
      // accepted" reasoning dashboard_grid_controller.js's own comment gives for a different case.
      info.revert()
    }
  }

  toDatetimeLocalValue(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  toFloatingString(date) {
    const pad = (n) => String(n).padStart(2, "0")
    return `${this.toDatetimeLocalValue(date)}:${pad(date.getSeconds())}`
  }
}
